/**
 * Experiment assignment utilities — cookie reader/writer + React context.
 *
 * The backend owns variant assignment (`GET /experiments/me/assignments`,
 * see `app/services/experiments.py` / `experiment_assignments` table —
 * Phase B/C of the portal-persistence rollout). AppLayout fetches that
 * map on mount and write-throughs it to the `exp_assignments` cookie
 * so synchronous sites of getExperimentVariant() — like the
 * post-purchase upsell hook — see the latest values without a context
 * subscription.
 *
 * Pre-auth marketing visitors are still cohorted client-side on
 * system-web (which writes the same cookie); when they sign up the
 * backend snapshots the cookie into experiment_assignments so the
 * variant carries through.
 *
 * What lived here pre-Phase-C and is gone now:
 *   - pickVariant() — backend owns the dice roll (deterministic by
 *     bucket = hash(user_id|experiment_key)). Removed to keep the
 *     selection logic in exactly one place.
 *   - cohortMissingExperiments() — replaced by the backend endpoint's
 *     fill-missing-on-call behavior.
 */

import { createContext, useContext } from 'react';

const COOKIE_NAME = 'exp_assignments';

// ── Read ────────────────────────────────────────────────────────────────────

export function readExperimentAssignments(): Record<string, string> {
  if (typeof document === 'undefined') return {};
  const raw = document.cookie || '';
  if (!raw) return {};
  const match = raw.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`));
  if (!match) return {};
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
  } catch {
    /* malformed cookie — fall through to empty */
  }
  return {};
}

/**
 * Legacy synchronous variant read. Reads the cookie directly — no React
 * context — so it works in plain TS modules outside the render tree
 * (`useShouldShowPostPurchaseUpsell` uses it in a useMemo). For
 * components inside the AppLayout tree, prefer `useExperimentVariant`
 * which subscribes to the backend-driven ExperimentsContext.
 */
export function getExperimentVariant(key: string): string | null {
  return readExperimentAssignments()[key] ?? null;
}

// ── Write ───────────────────────────────────────────────────────────────────

/**
 * Persist the assignment map to the `exp_assignments` cookie. Used by
 * AppLayout to write through the backend-resolved map so synchronous
 * readers (getExperimentVariant) see the same values without a context
 * subscription. Domain-widened to `.systemlabs.com` so the cookie is
 * shared across portal.systemlabs.com + the marketing site.
 */
export function writeExperimentAssignments(
  assignments: Record<string, string>,
): void {
  if (typeof document === 'undefined') return;
  const encoded = encodeURIComponent(JSON.stringify(assignments));
  const domainAttr = window.location.hostname.endsWith('.systemlabs.com')
    ? 'Domain=.systemlabs.com;'
    : '';
  document.cookie = `${COOKIE_NAME}=${encoded};Path=/;${domainAttr}SameSite=Lax;Max-Age=31536000`;
}

// ── Types (still exported — ExperimentDevPanel renders these shapes) ────────

export interface ActiveExperimentVariant {
  key: string;
  weight: number;
  enabled?: boolean;
}

export interface ActiveExperiment {
  key: string;
  experiment_type: string | null;
  audience?: string | null;
  variants: ActiveExperimentVariant[];
}

// ── React context ────────────────────────────────────────────────────────────

/**
 * Provides the current experiment assignments to all descendant components.
 * Populated by AppLayout from the backend response on mount + whenever
 * the user's assignments change.
 */
export const ExperimentsContext = createContext<Record<string, string>>({});

/**
 * Hook for reading a variant inside React — reactive to backend updates.
 * Prefer this over getExperimentVariant() in components rendered inside
 * AppLayout.
 */
export function useExperimentVariant(key: string): string | null {
  return useContext(ExperimentsContext)[key] ?? null;
}
